import { c as _c } from "react/compiler-runtime";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TbGripVertical } from 'react-icons/tb';
import { sortBy } from 'remeda';
import { keyBy } from 'shared/collection';
import { twMerge } from 'tailwind-merge';
type OrderableItem = {
  id: string;
};
const hasPosition = <T extends OrderableItem,>(item: T): item is T & {
  position: number;
} => 'position' in item;
const SortableItem = <T extends OrderableItem,>({
  item,
  component
}: {
  item: T;
  component: (item: T, state: {
    isDragging: boolean;
  }) => React.ReactNode;
}) => {
  const s = useSortable({
    id: item.id
  });
  return <div ref={s.setNodeRef} style={{
    transform: CSS.Translate.toString(s.transform),
    transition: s.transition,
    boxSizing: 'border-box' as const,
    width: s.isDragging ? 'auto' : undefined,
    minWidth: s.isDragging ? '100%' : undefined,
    zIndex: s.isDragging ? 100 : 0
  }} className={twMerge('flex w-full items-center relative', s.isDragging ? 'glass rounded-md' : '')}>
      <div {...s.attributes} {...s.listeners} className='p-1 cursor-grab active:cursor-grabbing flex-shrink-0'>
        <TbGripVertical size={18} className='stroke-gray-400' />
      </div>
      <div className='flex-1 min-w-0'>{component(item, {
        isDragging: s.isDragging
      })}</div>
    </div>;
};
export const OrderableList = t0 => {
  const $ = _c(14);
  const {
    data: dataRaw,
    component,
    onReorder
  } = t0;
  const data = dataRaw.map(_temp);
  const sortedData = sortBy(data, _temp2);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      coordinateGetter: sortableKeyboardCoordinates
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, t1));
  const handleDragEnd = event => {
    const {
      active,
      over
    } = event;
    if (over && active.id !== over.id) {
      const oldIndex = sortedData.findIndex(item => item.id === active.id);
      const newIndex = sortedData.findIndex(item_0 => item_0.id === over.id);
      const orderedData = arrayMove(sortedData, oldIndex, newIndex);
      const reOrdered = orderedData.map(_temp3);
      const byId = keyBy(data, _temp4);
      const positions = reOrdered.filter(r => r.position != byId[r.id]?.position);
      onReorder({
        positions,
        ids: reOrdered.map(_temp5),
        data: orderedData
      });
    }
  };
  const T0 = DndContext;
  const t2 = closestCenter;
  const T1 = SortableContext;
  const t3 = sortedData.map(_temp6);
  const t4 = verticalListSortingStrategy;
  let t5;
  if ($[1] !== component) {
    t5 = item_2 => <SortableItem key={item_2.id} item={item_2} component={component} />;
    $[1] = component;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const t6 = sortedData.map(t5);
  let t7;
  if ($[3] !== T1 || $[4] !== t3 || $[5] !== t4 || $[6] !== t6) {
    t7 = <T1 items={t3} strategy={t4}>{t6}</T1>;
    $[3] = T1;
    $[4] = t3;
    $[5] = t4;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== T0 || $[9] !== handleDragEnd || $[10] !== sensors || $[11] !== t2 || $[12] !== t7) {
    t8 = <T0 sensors={sensors} collisionDetection={t2} onDragEnd={handleDragEnd}>{t7}</T0>;
    $[8] = T0;
    $[9] = handleDragEnd;
    $[10] = sensors;
    $[11] = t2;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
};
export const moveById = <T,>(data: T[], from: T, to: T, by: (item: T) => any = item => item) => {
  const fromIndex = data?.findIndex(d => by(d) == by(from));
  const toIndex = data?.findIndex(d => by(d) == by(to));
  if (fromIndex == -1 || toIndex == -1) return data;
  const newData = [...data];
  newData.splice(toIndex, 0, newData.splice(fromIndex, 1)[0]!);
  return newData;
};
function _temp(d, i) {
  return hasPosition(d) ? d : {
    ...d,
    position: i
  };
}
function _temp2(d_0) {
  return d_0.position;
}
function _temp3(t0, i_0) {
  const {
    id
  } = t0;
  return {
    id,
    position: i_0
  };
}
function _temp4(i_1) {
  return i_1.id;
}
function _temp5(r_0) {
  return r_0.id;
}
function _temp6(item_1) {
  return item_1.id;
}